import { z } from 'zod';

import { toast } from '@/hooks/use-toast';
import { mergeHeaders, resolveRequestPath, withCsrf, invalidateCsrfToken } from '@/lib/security/csrf';
import { supabase } from '@/integrations/supabase/client';
import { getSupabaseAccessTokenSnapshot } from '@/integrations/supabase/sessionCookies';
import { buildApiUrl } from './utils';
import { getClientTimezone } from '@/lib/utils/timezone';
import { ApiResponseError, assertOk, parseJson } from './response';

type StoredAuthSessionPayload = {
  currentSession?: {
    access_token?: string | null;
  } | null;
};

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const BACKOFFICE_PREFIXES = [
  '/api/settings',
  '/api/staff',
  '/api/menu',
  '/api/inventory',
  '/api/tables',
  '/api/payments',
  '/api/orders',
  '/api/onboarding',
  '/api/tenant',
  '/api/autopilot',
  '/api/ai-staff',
];
const EXCLUDED_API_PREFIXES = ['/api/public', '/api/auth', '/api/webhooks'];
const EXCLUDED_ORIGINAL_PREFIXES = ['/r/'];
const CSRF_TOAST_COOLDOWN_MS = 10_000;
const AUTH_TOKEN_CACHE_TTL_MS =
  typeof import.meta !== 'undefined' &&
  typeof import.meta.env?.VITE_AUTH_TOKEN_CACHE_TTL_MS === 'string'
    ? Number(import.meta.env.VITE_AUTH_TOKEN_CACHE_TTL_MS) || 30_000
    : 30_000;

let lastCsrfToastAt = 0;
let cachedAuthToken: { value: string | null; expiresAt: number } = { value: null, expiresAt: 0 };
let resolvingAuthToken: Promise<string | null> | null = null;

const hasHeader = (headers: Record<string, string>, name: string): boolean => {
  const needle = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === needle);
};

const resolveSupabaseStorageToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storageKey =
    (supabase.auth as { storageKey?: string | null } | undefined)?.storageKey ?? null;
  const candidateKeys =
    storageKey && storageKey.length > 0
      ? [storageKey]
      : (() => {
          const url = supabase.restUrl ?? supabase.storageUrl ?? '';
          const match = url.match(/https?:\/\/([^.]+)\./i);
          return match ? [`sb-${match[1]}-auth-token`] : [];
        })();

  for (const key of candidateKeys) {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      continue;
    }
    try {
      const parsed = JSON.parse(raw) as StoredAuthSessionPayload;
      const token = parsed?.currentSession?.access_token ?? null;
      if (token) {
        return token;
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('[apiFetch] failed to parse Supabase storage key', { key, error });
      }
    }
  }

  return null;
};

const shouldAttachCsrf = (method: string, pathInfo: { original: string; api: string }): boolean => {
  if (!MUTATING_METHODS.has(method)) {
    return false;
  }

  if (EXCLUDED_ORIGINAL_PREFIXES.some((prefix) => pathInfo.original.startsWith(prefix))) {
    return false;
  }

  if (EXCLUDED_API_PREFIXES.some((prefix) => pathInfo.api.startsWith(prefix))) {
    return false;
  }

  return BACKOFFICE_PREFIXES.some((prefix) => pathInfo.api === prefix || pathInfo.api.startsWith(prefix + '/'));
};

const parseErrorCode = async (response: Response): Promise<string | null> => {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    const payload = (await response.json().catch(() => null)) as
      | { code?: unknown; error?: { code?: unknown } }
      | null;
    if (payload && typeof payload.code === 'string') {
      return payload.code;
    }
    if (payload && payload.error && typeof payload.error.code === 'string') {
      return payload.error.code;
    }
    return null;
  }

  const text = (await response.text().catch(() => '')).trim();
  if (!text) {
    return null;
  }
  return text.length <= 128 && text.toLowerCase().includes('csrf') ? text : null;
};

const isCsrfErrorCode = (code: string | null): boolean => {
  if (!code) {
    return false;
  }
  const lower = code.toLowerCase();
  return lower === 'csrf' || lower.startsWith('csrf_') || lower.includes('csrf');
};

const showReloadToast = (message: string): void => {
  const now = Date.now();
  if (now - lastCsrfToastAt < CSRF_TOAST_COOLDOWN_MS) {
    return;
  }
  lastCsrfToastAt = now;
  toast({
    title: 'Reload Required',
    description: message,
    variant: 'destructive',
  });
};

const resolveAuthToken = async (): Promise<string | null> => {
  const now = Date.now();
  if (cachedAuthToken.expiresAt > now) {
    return cachedAuthToken.value;
  }
  if (resolvingAuthToken) {
    return resolvingAuthToken;
  }

  resolvingAuthToken = (async () => {
    let token = getSupabaseAccessTokenSnapshot();

    if (!token) {
      try {
        const sessionResult = await supabase.auth.getSession();
        token = sessionResult.data.session?.access_token ?? null;
      } catch (error) {
        console.warn('[apiFetch] unable to resolve Supabase session token:', error);
      }
    }

    if (!token) {
      token = resolveSupabaseStorageToken();
    }

    if (!token) {
      token = getSupabaseAccessTokenSnapshot();
    }

    cachedAuthToken = {
      value: token,
      expiresAt: Date.now() + AUTH_TOKEN_CACHE_TTL_MS,
    };

    return token;
  })()
    .catch((error) => {
      cachedAuthToken = { value: null, expiresAt: 0 };
      throw error;
    })
    .finally(() => {
      resolvingAuthToken = null;
    });

  return resolvingAuthToken;
};

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { method: providedMethod, headers: providedHeaders, ...restInit } = init;
  const method = (providedMethod ?? 'GET').toString().toUpperCase();
  const url = buildApiUrl(path);
  const pathInfo = resolveRequestPath(path);
  const attachCsrf = shouldAttachCsrf(method, pathInfo);
  const maxAttempts = attachCsrf ? 2 : 1;

  let response: Response | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let headers = mergeHeaders(providedHeaders, {});

    if (!hasHeader(headers, 'accept')) {
      headers.Accept = 'application/json';
    }

    if (!hasHeader(headers, 'x-timezone')) {
      const timezone = getClientTimezone();
      if (timezone) {
        headers['X-Timezone'] = timezone;
      }
    }

    const body = restInit.body;
    const isFormDataBody = typeof FormData !== 'undefined' && body instanceof FormData;
    const isBlobBody = typeof Blob !== 'undefined' && body instanceof Blob;
    const isUrlEncoded = typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams;

    if (body !== undefined && !isFormDataBody && !isBlobBody && !isUrlEncoded && !hasHeader(headers, 'content-type')) {
      headers['Content-Type'] = 'application/json';
    }

    if (!hasHeader(headers, 'authorization')) {
      try {
        const token = await resolveAuthToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
          if (import.meta.env.DEV) {
            console.debug('[apiFetch] attach Supabase access token', { path, method });
          }
        } else if (import.meta.env.DEV) {
          console.debug('[apiFetch] no Supabase access token available for request', { path, method });
        }
      } catch (error) {
        console.warn('[apiFetch] failed to resolve auth token for request', { path, method, error });
      }
    }

    if (attachCsrf) {
      headers = await withCsrf(headers, { forceRefresh: attempt > 0 });
    }

    response = await fetch(url, {
      credentials: 'include',
      ...restInit,
      method,
      headers,
    });

    if (response.status !== 403) {
      return response;
    }

    const clone = response.clone();
    const errorCode = await parseErrorCode(clone);
    const csrfError = isCsrfErrorCode(errorCode);

    if (csrfError && attachCsrf && attempt + 1 < maxAttempts) {
      invalidateCsrfToken();
      continue;
    }

    const message = csrfError
      ? 'Your security session expired. Reload the app to refresh your access.'
      : 'Access was denied. Reload the app to refresh your session.';

    showReloadToast(message);
    return response;
  }

  return response as Response;
}

type JsonBody =
  | string
  | FormData
  | Blob
  | ArrayBufferView
  | ArrayBuffer
  | ReadableStream<Uint8Array>
  | URLSearchParams
  | null
  | undefined
  | Record<string, unknown>
  | unknown[];

interface RequestJsonOptions<T = unknown> extends Omit<RequestInit, 'body'> {
  body?: JsonBody;
  headers?: Record<string, string>;
  schema?: z.ZodType<T>;
}

const shouldSerializeBody = (body: JsonBody) => {
  if (body === undefined || body === null) return false;
  if (typeof body === 'string') return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  return true;
};

export async function requestJson<T>(
  path: string,
  { method = 'GET', body, headers = {}, schema, ...rest }: RequestJsonOptions<T> = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    if (shouldSerializeBody(body)) {
      init.body = JSON.stringify(body);
      init.headers = {
        'Content-Type': 'application/json',
        ...init.headers,
      };
    } else {
      init.body = body as BodyInit;
    }
  }

  const response = await apiFetch(path, init);

  if (response.status === 204) {
    if (schema) {
      const parsed = schema.safeParse(undefined);
      if (!parsed.success) {
        throw new ApiResponseError('Expected JSON response', response.status);
      }
      return parsed.data;
    }
    return undefined as T;
  }

  await assertOk(response);

  if (schema) {
    return parseJson(response, schema);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!/json/i.test(contentType)) {
    const preview = await response.text().catch(() => '');
    const snippet = preview.slice(0, 160).replace(/\s+/g, ' ').trim();
    const descriptor = contentType || 'unknown content type';
    throw new ApiResponseError(
      `Unexpected response from ${path}: expected JSON but received ${descriptor}.${snippet ? ` Body preview: "${snippet}".` : ''}`,
      response.status,
      preview || undefined,
    );
  }

  return (await response.json()) as T;
}
